import React, { useCallback, useMemo } from 'react';
import { Trash2 } from 'lucide-react';

import { formatPurchaseOrderDate } from 'components/procurement/components/purchaseOrdersV2/utils/purchaseOrderDateUtils';
import {
    formatNumber,
    normalizeOptionalText,
} from 'components/procurement/components/purchaseOrdersV2/utils/purchaseOrderDisplayUtils';
import { FC_TRANSFER_DISPLAY_FALLBACK } from 'components/warehouse/components/fcTransfers/fcTransferDisplayUtils';
import { Button } from 'shadcn/components/ui/Button';
import { Input } from 'shadcn/components/ui/Input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'shadcn/components/ui/Table';

/**
 * @param {Record<string, unknown>} snap
 * @returns {number | undefined}
 */
function readAvailableCases(snap) {
    const n = snap?.numCases ?? snap?.num_cases;
    if (n === undefined || n === null || n === '') return undefined;
    const parsed = Number(n);
    return Number.isFinite(parsed) && parsed >= 0 ? Math.floor(parsed) : undefined;
}

/**
 * Create flow line item (parent-owned).
 *
 * @typedef {Object} FCTransferCreateLineItem
 * @property {string} clientId
 * @property {number} lotId
 * @property {number} locationId
 * @property {Record<string, unknown>} inventorySnapshot - Row from transfer-inventory API at add time
 * @property {number} numCases
 * @property {number} numLooseUnits
 */

/**
 * Editable line items prior to POST (tech spec §6.5).
 *
 * @param {Object} props
 * @param {Array<FCTransferCreateLineItem>} [props.items=[]]
 * @param {(clientId: string, numCases: number) => void} [props.onNumCasesChange]
 * @param {(clientId: string, numLooseUnits: number) => void} [props.onNumLooseUnitsChange]
 * @param {(clientId: string) => void} [props.onRemoveRow]
 * @param {boolean} [props.disabled] - e.g. while save mutation is in flight
 * @param {boolean} [props.embedded] - When true, omit outer card chrome and title (nested under a parent card)
 */
export function FCTransferCreateItemsTable({
    disabled = false,
    embedded = false,
    items = [],
    onNumCasesChange,
    onNumLooseUnitsChange,
    onRemoveRow,
}) {
    const rows = useMemo(() => (Array.isArray(items) ? items : []), [items]);

    const parseNonNegativeInt = useCallback((raw) => {
        if (raw === '' || raw === undefined || raw === null) return 0;
        const n = typeof raw === 'number' ? raw : parseInt(String(raw), 10);
        if (!Number.isFinite(n) || n < 0) return 0;
        return Math.floor(n);
    }, []);

    const tableSection = (
        <div className="overflow-x-auto">
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead className="whitespace-nowrap text-left">Item</TableHead>
                        <TableHead className="whitespace-nowrap text-left">External SKU</TableHead>
                        <TableHead className="whitespace-nowrap text-left">Lot ID</TableHead>
                        <TableHead className="whitespace-nowrap text-left">BBD</TableHead>
                        <TableHead className="whitespace-nowrap text-left">Units / case</TableHead>
                        <TableHead className="whitespace-nowrap text-left">Quantity (cases)</TableHead>
                        <TableHead className="whitespace-nowrap text-left">Loose units</TableHead>
                        <TableHead className="w-12 text-left" aria-label="Actions" />
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {rows.length ? (
                        rows.map((row) => {
                            const snap = row.inventorySnapshot ?? {};
                            const itemName = snap.itemName ?? snap.item_name;
                            const externalSku = snap.externalSku ?? snap.external_sku;
                            const lotId = snap.lotId ?? snap.lot_id;
                            const expires = snap.expiresOn ?? snap.expires_on;
                            const unitsPerCase = snap.unitsPerCase ?? snap.units_per_case;
                            const maxCases = readAvailableCases(snap);

                            return (
                                <TableRow key={row.clientId}>
                                    <TableCell className="max-w-[200px] whitespace-normal break-words text-left">
                                        {normalizeOptionalText(itemName)}
                                    </TableCell>
                                    <TableCell className="whitespace-normal break-words text-left">
                                        {normalizeOptionalText(externalSku)}
                                    </TableCell>
                                    <TableCell className="text-left">{normalizeOptionalText(lotId)}</TableCell>
                                    <TableCell className="text-left">
                                        {formatPurchaseOrderDate(expires, FC_TRANSFER_DISPLAY_FALLBACK)}
                                    </TableCell>
                                    <TableCell className="text-left">
                                        {unitsPerCase === undefined || unitsPerCase === null || unitsPerCase === ''
                                            ? FC_TRANSFER_DISPLAY_FALLBACK
                                            : formatNumber(unitsPerCase, {
                                                  maximumFractionDigits: 2,
                                                  minimumFractionDigits: 0,
                                              })}
                                    </TableCell>
                                    <TableCell className="w-32 min-w-[8rem] text-left">
                                        <Input
                                            type="number"
                                            inputMode="numeric"
                                            min={0}
                                            max={maxCases !== undefined ? maxCases : undefined}
                                            disabled={disabled}
                                            className="tabular-nums"
                                            aria-label="Quantity in cases"
                                            value={Number.isFinite(row.numCases) ? String(row.numCases) : '0'}
                                            onChange={(e) =>
                                                onNumCasesChange?.(row.clientId, parseNonNegativeInt(e.target.value))
                                            }
                                        />
                                    </TableCell>
                                    <TableCell className="w-32 min-w-[8rem] text-left">
                                        <Input
                                            type="number"
                                            inputMode="numeric"
                                            min={0}
                                            disabled={disabled}
                                            className="tabular-nums"
                                            aria-label="Loose units"
                                            value={Number.isFinite(row.numLooseUnits) ? String(row.numLooseUnits) : '0'}
                                            onChange={(e) =>
                                                onNumLooseUnitsChange?.(
                                                    row.clientId,
                                                    parseNonNegativeInt(e.target.value),
                                                )
                                            }
                                        />
                                    </TableCell>
                                    <TableCell className="text-left">
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="icon"
                                            className="text-muted-foreground hover:text-destructive"
                                            disabled={disabled}
                                            aria-label="Remove line item"
                                            onClick={() => onRemoveRow?.(row.clientId)}
                                        >
                                            <Trash2 className="h-4 w-4" aria-hidden="true" />
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            );
                        })
                    ) : (
                        <TableRow>
                            <TableCell colSpan={8} className="h-24 text-center text-muted-foreground">
                                Add lots from the picker above to build this transfer.
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </div>
    );

    if (embedded) {
        return tableSection;
    }

    return (
        <div className="rounded-md border bg-card p-6 shadow-sm">
            <div className="flex flex-col gap-4">
                <h2 className="text-left text-lg font-semibold tracking-tight text-foreground">Line items</h2>
                {tableSection}
            </div>
        </div>
    );
}

FCTransferCreateItemsTable.displayName = 'FCTransferCreateItemsTable';
